define('salesInboxStatsView',[
  'module',
  'app',
  'marionette',
  'underscore',
  'moment',
  'template!salesInboxStatsTpl',
  'salesInboxBarometer',
  'entities/sales_inbox'
], function (Module, App, Marionette, _, moment, viewTpl, Barometer) {
  'use strict';

  // ============================================================
  // SALES INBOX — Stats ("Pulse") screen.
  //
  // Money-first read on the inbox: revenue exposure weighted by
  // SLA health (the hero, rhyming with the list screen's urgency
  // rail), real-time channel health (resolved chats & calls), value
  // by category, the lifecycle funnel, and a per-user pace
  // leaderboard that includes each agent's channel split.
  //
  // The backend `salesInbox:stats` endpoint currently returns counts
  // only. Every value-weighted / channel / median field below is
  // read defensively: when present it lights up, when absent the UI
  // degrades to counts or an em-dash. See normalize() for the exact
  // shape consumed.
  // ============================================================

  var RANGES = [
    { key: 'TODAY', label: 'Today' },
    { key: '7D', label: '7 days' },
    { key: '30D', label: '30 days' }
  ];

  var CATEGORY_META = [
    { key: 'SALES', label: 'Sales', color: '#3b5bdb' },
    { key: 'DESIGN', label: 'Design', color: '#7c5cff' },
    { key: 'RECLAMATION', label: 'Reclamation', color: '#e5484d' },
    { key: 'INVOICING', label: 'Invoicing', color: '#0e9f9f' },
    { key: 'TRACKING', label: 'Tracking', color: '#e8a33d' },
    { key: 'NOT_ACTIONABLE', label: 'Not actionable', color: '#9aa4b2' },
    { key: 'SUPPLIER_ORDER', label: 'Supplier order', color: '#2f7d5b' },
    { key: 'SUPPLIER_SHIPPING', label: 'Supplier shipping', color: '#d97706' },
    { key: 'SUPPLIER_PROOF', label: 'Supplier proof', color: '#9c36b5' },
    { key: 'SUPPLIER_INVOICE', label: 'Supplier invoice', color: '#0b7285' },
    { key: 'SUPPLIER_CLAIM', label: 'Supplier claim', color: '#c92a2a' }
  ];

  // EmailCase breakdown shown under each category in "Where the value sits".
  // Keyed by category; each entry maps the backend enum name to a readable label.
  var CASE_META = {
    SALES: [
      { key: 'OFFER_REQUEST', label: 'Offer request' },
      { key: 'PRICE_NEGOTIATION', label: 'Price negotiation' },
      { key: 'OFFER_DECLINED', label: 'Offer declined' },
      { key: 'PURCHASE_ORDER', label: 'Purchase order' },
      { key: 'REORDER', label: 'Reorder' },
      { key: 'READY_TO_ORDER', label: 'Ready to order' },
      { key: 'DELIVERY_TIME', label: 'Delivery time' },
      { key: 'QUANTITY', label: 'Quantity' },
      { key: 'SAMPLE', label: 'Sample' },
      { key: 'STOCKS', label: 'Stocks' },
      { key: 'EXPRESS_DELIVERY', label: 'Express delivery' },
      { key: 'CHANGE', label: 'Change' },
      { key: 'UNFINISHED_ORDER', label: 'Unfinished order' },
      { key: 'UNDER_REVIEW', label: 'Under review' }
    ],
    DESIGN: [
      { key: 'ASK_FOR_MODIFICATION', label: 'Ask for modification' },
      { key: 'LOGO_NOT_PRINTABLE', label: 'Logo not printable' },
      { key: 'NO_LOGO_SENT', label: 'No logo sent' }
    ],
    INVOICING: [
      { key: 'ASK_FOR_INVOICE', label: 'Ask for invoice' },
      { key: 'PAY_AFTER_DELIVERY', label: 'Pay after delivery' },
      { key: 'CHANGE_DATA', label: 'Change data' },
      { key: 'ASK_FOR_RIB', label: 'Ask for RIB' },
      { key: 'PAYMENT_RECEIVED', label: 'Payment received' }
    ],
    RECLAMATION: [
      { key: 'PRINTING_ISSUE', label: 'Printing issue' },
      { key: 'BROKEN_GOODS', label: 'Broken goods' },
      { key: 'MISSING_GOODS', label: 'Missing goods' }
    ],
    TRACKING: [
      { key: 'WHEN_WILL_ARRIVE', label: 'When will it arrive' },
      { key: 'DID_NOT_ARRIVE', label: 'Did not arrive' },
      { key: 'PROBLEM', label: 'Problem' }
    ],
    NOT_ACTIONABLE: [
      { key: 'OUT_OF_OFFICE', label: 'Out of office' },
      { key: 'EMAIL_NOT_DELIVERED', label: 'Email not delivered' },
      { key: 'CARRIER_SURVEY', label: 'Carrier survey' },
      { key: 'VENDOR_SOLICITATION', label: 'Vendor solicitation' }
    ],
    SUPPLIER_ORDER: [
      { key: 'ORDER_CONFIRMED', label: 'Order confirmed' },
      { key: 'ORDER_DELAYED', label: 'Order delayed' },
      { key: 'OUT_OF_STOCK', label: 'Out of stock' },
      { key: 'SUPPLIER_QUESTION', label: 'Supplier question' }
    ],
    SUPPLIER_SHIPPING: [
      { key: 'SHIPPED', label: 'Shipped' },
      { key: 'SHIPPING_ISSUE', label: 'Shipping issue' }
    ],
    SUPPLIER_PROOF: [
      { key: 'PROOF_TO_VALIDATE', label: 'Proof to validate' },
      { key: 'ARTWORK_ISSUE', label: 'Artwork issue' }
    ],
    SUPPLIER_INVOICE: [
      { key: 'INVOICE_RECEIVED', label: 'Invoice received' },
      { key: 'PAYMENT_REMINDER', label: 'Payment reminder' },
      { key: 'CREDIT_NOTE', label: 'Credit note' }
    ],
    SUPPLIER_CLAIM: [
      { key: 'CLAIM_UPDATE', label: 'Claim update' }
    ]
  };

  var STATUS_META = [
    { key: 'NEW', label: 'New', color: '#3b5bdb' },
    { key: 'AWAITING', label: 'Awaiting', color: '#e8a33d' },
    { key: 'RESOLVED', label: 'Resolved', color: '#9aa4b2', muted: true }
  ];

  var AVATAR_COLORS = ['#3b5bdb', '#7c5cff', '#0e9f9f', '#c98a1a', '#2f9e6f', '#e5484d'];

  // Team-table sort: maps each sortable column to the raw value it sorts on.
  // Nulls always sort to the bottom regardless of direction.
  var SORT_ACCESSORS = {
    user: function (u) { return (u.name || '').toLowerCase(); },
    open: function (u) { return u.open; },
    resolved: function (u) { return u.resolved; },
    resp: function (u) { return u.respRaw; },
    chat: function (u) { return u.chatResolved; },
    calls: function (u) { return u.callsResolved; },
    oldest: function (u) { return u.oldestRaw; }
  };

  // null-or-undefined guards (eqeqeq-safe; JSON keys may be absent)
  function isNil(x) { return x === null || x === undefined; }
  function notNil(x) { return x !== null && x !== undefined; }

  // ── formatting helpers ────────────────────────────────────────
  function eurFull(n) {
    if (isNil(n)) return '—';
    return '€' + Number(Math.round(n)).toLocaleString('en-US');
  }
  function eurCompact(n) {
    if (isNil(n)) return '—';
    if (Math.abs(n) >= 1000) return '€' + (n / 1000).toFixed(1) + 'k';
    return '€' + Math.round(n);
  }
  function humanizeMinutes(min) {
    if (isNil(min)) return '—';
    if (min < 60) return min + 'm';
    var h = Math.floor(min / 60);
    if (h < 24) {
      var rm = min % 60;
      return rm ? h + 'h' + (rm < 10 ? '0' : '') + rm : h + 'h';
    }
    var d = Math.floor(h / 24);
    var rh = h % 24;
    return rh ? d + 'd ' + rh + 'h' : d + 'd';
  }
  function humanizeSeconds(s) {
    if (isNil(s)) return '—';
    if (s < 60) return Math.round(s) + 's';
    var m = Math.floor(s / 60);
    var rs = Math.round(s % 60);
    return rs ? m + 'm' + (rs < 10 ? '0' : '') + rs : m + 'm';
  }
  function initials(name) {
    if (!name) return '';
    var parts = String(name).trim().split(/\s+/);
    var s = (parts[0] || '').charAt(0) + (parts.length > 1 ? parts[parts.length - 1].charAt(0) : '');
    return s.toUpperCase();
  }
  // accept either a plain number or a {value,count} bucket
  function bucketValue(x) {
    if (isNil(x)) return null;
    if (typeof x === 'number') return x;
    return notNil(x.value) ? x.value : null;
  }
  function bucketCount(x) {
    if (isNil(x)) return null;
    if (typeof x === 'number') return null;
    return notNil(x.count) ? x.count : null;
  }

  Module.exports = Marionette.LayoutView.extend({
    className: 'sales-inbox-stats-view',

    ui: {
      rangeBtn: '.range-btn',
      userPicker: '.user-picker',
      localePicker: '.locale-picker',
      dtFrom: '.dt-from',
      dtTo: '.dt-to',
      dtApply: '.dt-apply',
      sortHeader: '.th-sortable'
    },

    events: {
      'click @ui.rangeBtn': 'onRangeClick',
      'change @ui.userPicker': 'onUserChange',
      'change @ui.localePicker': 'onLocaleChange',
      'click @ui.dtApply': 'onCustomApply',
      'click @ui.sortHeader': 'onSortClick'
    },

    initialize: function (options) {
      // Sales or support inbox (same stats, one inbox at a time).
      this.inbox = (options && options.inbox) === 'SUPPORT' ? 'SUPPORT' : 'SALES';
      this.range = '7D';
      // Team-table sort ('' = backend order). Numeric columns default to
      // descending (busiest first); the user/name column to ascending.
      this.sortField = '';
      this.sortDesc = true;
      // 'preset' = one of the quick ranges; 'custom' = the date-time picker.
      this.mode = 'preset';
      // Custom window (ISO strings) when mode === 'custom'.
      this.customFrom = null;
      this.customTo = null;
      this.statsData = null;
      this.loading = true;
      // Selected user to scope the whole screen to ('' = all users).
      this.userSecId = '';
      // Stable picker options, captured from the unscoped (all-users) load
      // so the dropdown keeps every agent even while scoped to one.
      this.allUsers = [];
      // Selected locale to scope email + chat stats to ('' = all locales).
      this.localeSel = '';
      this.allLocales = [];
    },

    onRangeClick: function (e) {
      var key = e.currentTarget.getAttribute('data-range');
      if (!key || (this.mode === 'preset' && key === this.range)) return;
      this.range = key;
      this.mode = 'preset';
      this.loading = true;
      this.render();
      this.load();
    },

    // Apply the custom date-time window. Inputs are <input type="datetime-local">
    // ('YYYY-MM-DDTHH:mm', local time); ignore unless both are present and from < to.
    onCustomApply: function () {
      var fromVal = (this.ui.dtFrom.val() || '').trim();
      var toVal = (this.ui.dtTo.val() || '').trim();
      var from = fromVal ? moment(fromVal) : null;
      var to = toVal ? moment(toVal) : null;

      var fromOk = false;
      if (from) { fromOk = from.isValid(); }
      var toOk = false;
      if (to) { toOk = to.isValid(); }
      var ordered = false;
      if (fromOk && toOk) { ordered = from.isBefore(to); }

      if (!fromOk || !toOk || !ordered) {
        this.ui.dtFrom.toggleClass('invalid', !fromOk || (toOk && !ordered));
        this.ui.dtTo.toggleClass('invalid', !toOk || (fromOk && !ordered));
        return;
      }
      this.ui.dtFrom.removeClass('invalid');
      this.ui.dtTo.removeClass('invalid');
      this.customFrom = from.toISOString();
      this.customTo = to.toISOString();
      this.mode = 'custom';
      this.loading = true;
      this.render();
      this.load();
    },

    onUserChange: function (e) {
      var val = e.currentTarget.value || '';
      if (val === this.userSecId) return;
      this.userSecId = val;
      this.loading = true;
      this.render();
      this.load();
    },

    onLocaleChange: function (e) {
      var val = e.currentTarget.value || '';
      if (val === this.localeSel) return;
      this.localeSel = val;
      this.loading = true;
      this.render();
      this.load();
    },

    // Team-table column sort. Click a header to sort by it; click again to flip
    // direction. Purely client-side on the already-loaded data — no reload.
    onSortClick: function (e) {
      var field = e.currentTarget.getAttribute('data-sort');
      if (!field) return;
      if (this.sortField === field) {
        this.sortDesc = !this.sortDesc;
      } else {
        this.sortField = field;
        this.sortDesc = field !== 'user';
      }
      this.render();
    },

    onBeforeShow: function () { this.load(); },

    // Resolve the active selection into a concrete {from, to} moment window.
    // A custom window wins when set; otherwise the preset maps to [start, now].
    windowMoments: function () {
      if (this.mode === 'custom' && this.customFrom && this.customTo) {
        return { from: moment(this.customFrom), to: moment(this.customTo) };
      }
      var to = moment();
      var from = moment().subtract(7, 'days').startOf('day'); // 7D is the default window
      if (this.range === 'TODAY') {
        from = moment().startOf('day');
      } else if (this.range === '30D') {
        from = moment().subtract(30, 'days').startOf('day');
      }
      return { from: from, to: to };
    },

    // Translate the active window into the from/to params the stats endpoint
    // understands (it has no `range` param). Sent as ISO-8601 (UTC) so the
    // backend's @DateTimeFormat(ISO.DATE_TIME) parses it deterministically.
    // userSecId scopes every aggregate to one assigned user when set.
    queryParams: function () {
      var w = this.windowMoments();
      var params = { from: w.from.toISOString(), to: w.to.toISOString() };
      if (this.userSecId) { params.userSecId = this.userSecId; }
      if (this.localeSel) { params.locale = this.localeSel; }
      if (this.inbox === 'SUPPORT') { params.inbox = 'SUPPORT'; }
      return params;
    },

    load: function () {
      var self = this;
      App.request('salesInbox:stats', this.queryParams()).done(function (data) {
        self.statsData = data || {};
        // Refresh the picker roster only from the unscoped response, so the
        // dropdown still lists everyone once we've drilled into one user.
        if (!self.userSecId && _.isArray(self.statsData.byUser)) {
          self.allUsers = _.chain(self.statsData.byUser)
            .filter(function (u) { return u.userpidSecId && u.userpidName; })
            .map(function (u) { return { secId: u.userpidSecId, name: u.userpidName }; })
            .sortBy(function (u) { return u.name.toLowerCase(); })
            .value();
        }
        // Locale dropdown options, captured from a load that isn't locale-scoped
        // (byLocale is never self-filtered) so every present locale stays listed.
        if (!self.localeSel && self.statsData.byLocale) {
          self.allLocales = _.chain(_.keys(self.statsData.byLocale))
            .map(function (code) { return { code: code, label: code.toUpperCase() }; })
            .sortBy(function (l) { return l.label; })
            .value();
        }
        self.loading = false;
        self.render();
      }).fail(function () {
        self.statsData = {};
        self.loading = false;
        self.render();
      });
    },

    // Map whatever the endpoint returns into the view model the
    // template renders. Value-weighted / channel / median fields are
    // optional; counts (byStatus, byCategory, byUser, slaBreached,
    // totalOpen) are the guaranteed baseline.
    normalize: function () {
      var d = this.statsData || {};
      var byStatus = d.byStatus || {};
      var byCategory = d.byCategory || {};
      var valueByCategory = d.valueByCategory || {};
      var totalOpen = d.totalOpen || 0;
      var slaBreached = d.slaBreached || 0;

      // ── hero + exposure rail ─────────────────────────────────
      var openValue = notNil(d.openValue) ? d.openValue : null;
      var hero = {
        eyebrow: 'Revenue exposure' + (totalOpen ? ' · ' + totalOpen + ' open items' : ''),
        num: notNil(openValue) ? eurFull(openValue) : String(totalOpen),
        unit: notNil(openValue) ? 'open value' : 'open items'
      };

      // slaHealth: optional [{key, value, count}]. Fall back to the
      // on-track / breached split we can derive from counts today.
      var health = _.isArray(d.slaHealth) && d.slaHealth.length ? d.slaHealth : [
        { key: 'onTrack', count: Math.max(totalOpen - slaBreached, 0) },
        { key: 'breached', count: slaBreached }
      ];
      var healthMeta = {
        onTrack: { cls: 'calm', label: 'On track' },
        dueSoon: { cls: 'warn', label: 'Due soon' },
        breached: { cls: 'hot', label: 'Past SLA' }
      };
      var useValue = _.every(health, function (h) { return notNil(bucketValue(h)) || notNil(h.value); });
      var weights = _.map(health, function (h) {
        return useValue ? (notNil(h.value) ? h.value : bucketValue(h)) : (h.count || 0);
      });
      var weightTotal = _.reduce(weights, function (a, b) { return a + (b || 0); }, 0) || 1;
      var rail = {
        segments: _.map(health, function (h, i) {
          var m = healthMeta[h.key] || { cls: 'cool', label: h.key };
          return {
            cls: 's-' + m.cls,
            w: Math.round((weights[i] / weightTotal) * 1000) / 10,
            label: useValue ? eurCompact(notNil(h.value) ? h.value : bucketValue(h)) : String(h.count || 0)
          };
        }),
        legend: _.map(health, function (h) {
          var m = healthMeta[h.key] || { cls: 'cool', label: h.key };
          return { cls: m.cls, label: m.label, count: notNil(h.count) ? h.count : '—' };
        })
      };

      // ── pace tiles ───────────────────────────────────────────
      var resolvedToday = notNil(d.resolvedToday) ? d.resolvedToday : null;
      var inflowToday = notNil(d.inflowToday) ? d.inflowToday : null;
      var backlogDelta = (notNil(resolvedToday) && notNil(inflowToday)) ? (inflowToday - resolvedToday) : null;
      var vip = d.vipBigOrderOpen || null;
      var vipCountFallback = (d.byPotential && d.byPotential.BIG_ORDER_VALUE) || null;
      var tiles = {
        median: humanizeMinutes(notNil(d.medianFirstResponseMinutes) ? d.medianFirstResponseMinutes : null),
        medianDelta: notNil(d.medianFirstResponseDeltaMinutes) ? d.medianFirstResponseDeltaMinutes : null,
        resolvedToday: notNil(resolvedToday) ? resolvedToday : '—',
        inflowToday: notNil(inflowToday) ? inflowToday : null,
        backlogDelta: backlogDelta,
        vipNum: vip ? vip.count : (notNil(vipCountFallback) ? vipCountFallback : '—'),
        vipCount: vip && notNil(vip.vipCount) ? vip.vipCount : null,
        vipValue: vip && notNil(vip.value) ? eurCompact(vip.value) : null
      };

      // ── real-time channels ───────────────────────────────────
      // Chat & calls track resolved/handled volume only — there is no
      // "missed" concept for these channels.
      var ch = d.channels || {};
      function channelVm(key, name, icon, raw, secLabel, secFmt) {
        raw = raw || {};
        var resolved = notNil(raw.resolved) ? raw.resolved : null;
        return {
          key: key, name: name, icon: icon,
          has: notNil(resolved),
          resolved: notNil(resolved) ? resolved : '—',
          secLabel: secLabel,
          secVal: secFmt
        };
      }
      var channels = [
        channelVm('chat', 'Chat', 'ti-message-2', ch.chat, 'Avg pickup',
          ch.chat ? humanizeSeconds(ch.chat.avgPickupSeconds) : '—'),
        channelVm('calls', 'Calls', 'ti-phone', ch.calls, 'Avg ring',
          ch.calls ? humanizeSeconds(ch.calls.avgRingSeconds) : '—')
      ];

      // ── value by category (+ emailCase breakdown) ────────────
      var byCase = d.byCase || {};
      var cats = [];
      _.each(CATEGORY_META, function (m) {
        var value = bucketValue(valueByCategory[m.key]);
        var count = byCategory[m.key];
        if (isNil(value) && isNil(count)) return;
        // Cases belonging to this category that actually occur, busiest first.
        var cases = [];
        _.each(CASE_META[m.key] || [], function (cm) {
          var cnt = byCase[cm.key];
          if (isNil(cnt) || !cnt) return;
          cases.push({ label: cm.label, count: cnt });
        });
        cases.sort(function (a, b) { return b.count - a.count; });
        cats.push({
          label: m.label, color: m.color, value: value,
          count: notNil(count) ? count : (bucketCount(valueByCategory[m.key]) || 0),
          cases: cases
        });
      });
      var catUseValue = _.every(cats, function (c) { return notNil(c.value); }) && cats.length > 0;
      var catMax = _.reduce(cats, function (a, c) {
        var w = catUseValue ? c.value : c.count;
        return Math.max(a, w || 0);
      }, 0) || 1;
      _.each(cats, function (c) {
        var w = catUseValue ? c.value : c.count;
        c.w = Math.round(((w || 0) / catMax) * 1000) / 10;
        c.valueLabel = catUseValue ? eurCompact(c.value) : (c.count + ' items');
      });

      // ── lifecycle funnel ─────────────────────────────────────
      var seen = {};
      var lifecycle = [];
      _.each(STATUS_META, function (m) {
        if (isNil(byStatus[m.key])) return;
        seen[m.key] = true;
        lifecycle.push({ label: m.label, color: m.color, muted: !!m.muted, count: byStatus[m.key] });
      });
      _.each(byStatus, function (v, k) {
        if (seen[k]) return;
        lifecycle.push({ label: k, color: '#9aa4b2', muted: false, count: v });
      });
      var lifeMax = _.reduce(lifecycle, function (a, l) { return Math.max(a, l.count || 0); }, 0) || 1;
      _.each(lifecycle, function (l) { l.w = Math.round((l.count / lifeMax) * 1000) / 10; });

      // ── per-user pace + channel split ────────────────────────
      var users = _.map(d.byUser || [], function (u, i) {
        var name = u.userpidName || '';
        var unassigned = !name || /^unassigned$/i.test(name);
        return {
          name: unassigned ? 'Unassigned' : name,
          unassigned: unassigned,
          initials: initials(name),
          avatarColor: AVATAR_COLORS[i % AVATAR_COLORS.length],
          open: notNil(u.openCount) ? u.openCount : 0,
          resolved: notNil(u.resolvedCount) ? u.resolvedCount : 0,
          median: humanizeMinutes(notNil(u.avgFirstResponseMinutes) ? u.avgFirstResponseMinutes : null),
          respRaw: notNil(u.avgFirstResponseMinutes) ? u.avgFirstResponseMinutes : null,
          chatResolved: notNil(u.chatResolved) ? u.chatResolved : null,
          callsResolved: notNil(u.callsResolved) ? u.callsResolved : null,
          oldest: humanizeMinutes(notNil(u.oldestItemAgeMinutes) ? u.oldestItemAgeMinutes : null),
          oldestRaw: notNil(u.oldestItemAgeMinutes) ? u.oldestItemAgeMinutes : null,
          oldestHot: notNil(u.oldestItemAgeMinutes) && u.oldestItemAgeMinutes > 1440
        };
      });

      // Apply the selected column sort (nulls always last).
      var sortAcc = this.sortField ? SORT_ACCESSORS[this.sortField] : null;
      if (sortAcc) {
        var sortDesc = this.sortDesc;
        users.sort(function (a, b) {
          var av = sortAcc(a), bv = sortAcc(b);
          if (isNil(av) && isNil(bv)) return 0;
          if (isNil(av)) return 1;
          if (isNil(bv)) return -1;
          if (typeof av === 'string' || typeof bv === 'string') {
            var r = String(av).localeCompare(String(bv));
            return sortDesc ? -r : r;
          }
          return sortDesc ? (bv - av) : (av - bv);
        });
      }

      // ── queue barometer by locale ────────────────────────────
      // Shared per-locale barometer. Hidden when a single locale is filtered
      // (a one-locale view makes the cross-locale comparison pointless).
      var barometerHtml = this.localeSel ? '' : Barometer.html(Barometer.build(d));

      // Picker roster + which user (if any) the screen is scoped to.
      var selectedUser = _.findWhere(this.allUsers, { secId: this.userSecId });
      var isPreset = this.mode === 'preset';
      // Prefill the custom inputs with the active window (datetime-local format,
      // local time) so the picker opens on a sensible starting point.
      var win = this.windowMoments();
      return {
        loading: this.loading,
        ranges: _.map(RANGES, function (r) { return { key: r.key, label: r.label, on: isPreset && r.key === this.range }; }, this),
        customOn: this.mode === 'custom',
        dtFromVal: win.from.format('YYYY-MM-DDTHH:mm'),
        dtToVal: win.to.format('YYYY-MM-DDTHH:mm'),
        userOptions: this.allUsers,
        selectedUserSecId: this.userSecId || '',
        scopedUserName: selectedUser ? selectedUser.name : null,
        localeOptions: this.allLocales,
        selectedLocale: this.localeSel || '',
        hero: hero,
        rail: rail,
        tiles: tiles,
        channels: channels,
        cats: cats,
        lifecycle: lifecycle,
        users: users,
        barometerHtml: barometerHtml,
        sort: { field: this.sortField, desc: this.sortDesc }
      };
    },

    serializeData: function () {
      return this.normalize();
    },

    template: viewTpl
  });
});

